import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserSidebarLayout } from '../components/UserSidebarLayout';
import { COLORS } from '../../../core/constants/colors';
import { Company, Query } from '../types';
import { api } from '../../../core/utils/apiClient';
import { linkedInEnrichmentService } from '../../linkedin_enrichment/services/linkedin-enrichment.service';
import { LinkedInEnrichmentData } from '../../linkedin_enrichment/types';
import { webEnrichmentService } from '../../web_enrichment/services/web-enrichment.service';
import { WebEnrichmentData } from '../../web_enrichment/types';
import { RecommendationsSection } from '../../recommendations/components/RecommendationsSection';
import { CompanyMembersManager } from '../../dashboard_user/components/CompanyMembersManager';
import { useAuth } from '../../../app/providers/AuthProvider';
import { LinkedInRecentPosts } from '../../companies/components/LinkedInRecentPosts';
import { LinkedInProfileDisplay } from '../../companies/components/LinkedInProfileDisplay';
import { WebEnrichmentDisplay } from '../../companies/components/WebEnrichmentDisplay';
import { WebOverridePendingReview } from '../../companies/components/WebOverridePendingReview';
import { EditCompanyModal } from '../../companies/components/EditCompanyModal';
import { CompanyOverviewTab } from '../../companies/components/CompanyOverviewTab';
import { EditLeadershipTab } from '../../admin/companies/components/EditLeadershipTab';
import { EnrichmentActionModal } from '../../admin/companies/components/EnrichmentActionModal';
import { useLeadership } from '../../admin/companies/hooks/useLeadership';

export const CompanyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [company, setCompany] = useState<Company | null>(null);
  const { leaders: apiLeaders, isLoading: isLoadingLeaders } = useLeadership(company?.id ?? 0);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('about');
  const [queryText, setQueryText] = useState('');
  const [isSubmittingQuery] = useState(false);
  const [queryHistory, setQueryHistory] = useState<Query[]>([]);
  const [linkedInData, setLinkedInData] = useState<LinkedInEnrichmentData | null>(null);
  const [isLoadingLinkedIn, setIsLoadingLinkedIn] = useState(false);
  const [webEnrichmentData, setWebEnrichmentData] = useState<WebEnrichmentData | null>(null);
  const [isLoadingWebEnrichment, setIsLoadingWebEnrichment] = useState(false);
  const [showMembersManager, setShowMembersManager] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState<string>('viewer');
  const [showEditCompanyModal, setShowEditCompanyModal] = useState(false);
  const [showEnrichMenu, setShowEnrichMenu] = useState(false);
  const [enrichModal, setEnrichModal] = useState<{ type: 'web' | 'linkedin'; open: boolean }>({ type: 'web', open: false });
  const [isEditingLeadership, setIsEditingLeadership] = useState(false);

  // Close enrich menu on outside click
  useEffect(() => {
    if (!showEnrichMenu) return;
    const handler = () => setShowEnrichMenu(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [showEnrichMenu]);
  const [scoringInProgress, setScoringInProgress] = useState(false);

  useEffect(() => {
    loadCompanyData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Poll for enrichment scoring completion — refresh company when it finishes
  useEffect(() => {
    if (!id || !scoringInProgress) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/api/v1/companies/${id}`);
        if (res.ok) {
          const updated: Company = await res.json();
          if (updated.enrichment_score !== company?.enrichment_score ||
              updated.profile_completeness_score !== company?.profile_completeness_score) {
            setCompany(updated);
            setScoringInProgress(false);
          }
        }
      } catch {
        // non-fatal — keep polling
      }
    }, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, scoringInProgress]);

  const loadCompanyData = async () => {
    try {
      setIsLoading(true);

      // Load company details
      const companyResponse = await api.get(`/api/v1/companies/${id}`);

      if (companyResponse.ok) {
        const companyData = await companyResponse.json();
        setCompany(companyData);
      }

      // Load current user's role in this company
      try {
        const membersResponse = await api.get(`/api/v1/companies/${id}/members`);
        if (membersResponse.ok) {
          const members = await membersResponse.json();
          const currentUserMember = members.find((m: any) => m.user_id === authState.user?.email || m.user_id === authState.user?.id);
          if (currentUserMember) {
            setCurrentUserRole(currentUserMember.role);
          }
        }
      } catch (err) {
        console.error('Error loading members:', err);
      }

      // Load query history for this company
      const historyResponse = await api.get('/api/v1/llm/query/history?limit=10');

      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        const companyQueries = (historyData || [])
          .filter((q: Query) => q.company_id === parseInt(id!));
        setQueryHistory(companyQueries);
      }

      // Load LinkedIn data for this company
      setIsLoadingLinkedIn(true);
      try {
        const linkedInResponse = await linkedInEnrichmentService.getData(parseInt(id!));
        setLinkedInData(linkedInResponse);
      } catch (error) {
        console.error('Error loading LinkedIn data:', error);
        // LinkedIn data is optional, so we don't fail the entire page load
      } finally {
        setIsLoadingLinkedIn(false);
      }

      // Load Web enrichment data for this company
      setIsLoadingWebEnrichment(true);
      try {
        const webEnrichmentResponse = await webEnrichmentService.getData(parseInt(id!));
        setWebEnrichmentData(webEnrichmentResponse);
      } catch (error) {
        console.error('Error loading web enrichment data:', error);
        // Web enrichment data is optional, so we don't fail the entire page load
      } finally {
        setIsLoadingWebEnrichment(false);
      }
    } catch (error) {
      console.error('Error loading company data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitQuery = (e: React.FormEvent) => {
    e.preventDefault();
    const text = queryText.trim();
    if (!text || !company?.id) return;

    // Hand the question to the chat page scoped to this company. The chat page
    // auto-submits the pendingQuery; the backend scopes the new conversation to
    // companyId so the agent answers about this company specifically.
    setQueryText('');
    navigate('/dashboard/chat', {
      state: {
        pendingQuery: text,
        isNewQuery: true,
        newChat: true,
        companyId: company.id,
      },
    });
  };

  const getCompanyInitial = (name: string) => {
    return name?.charAt(0).toUpperCase() || 'C';
  };


  const canManageCompany = currentUserRole === 'owner' || currentUserRole === 'admin';

  if (isLoading) {
    return (
      <UserSidebarLayout>
      <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5', fontFamily: 'DM Sans, sans-serif' }}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
              style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
            />
            <p style={{ color: COLORS.primary.persianIndigo }}>Loading company...</p>
          </div>
        </div>
      </div>
      </UserSidebarLayout>
    );
  }

  if (!company) {
    return (
      <UserSidebarLayout>
      <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5', fontFamily: 'DM Sans, sans-serif' }}>
        <div className="text-center py-12">
          <p className="text-gray-600">Company not found</p>
        </div>
      </div>
      </UserSidebarLayout>
    );
  }

  return (
    <UserSidebarLayout>
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5', fontFamily: 'DM Sans, sans-serif' }}>
      {/* Azure Header */}
      <div style={{ backgroundColor: COLORS.user.primary }} className="relative">
        {/* Decorative background, clipped to the header so the oversized logo
            doesn't bleed out. Kept in its OWN overflow-hidden layer so it does
            not clip the Enrich dropdown (absolute, z-50) in the content layer. */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Large Tilted Send Icon Background */}
          <div
            className="absolute right-0 top-0 bottom-0 flex items-center"
            style={{ opacity: 0.18 }}
          >
            <img
              src="/new_logo_white_P.png"
              alt="Portfolio Indexing System"
              className="h-96 opacity-50"
            />
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-8 py-6 relative z-10">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 text-sm text-white mb-6" style={{ opacity: 0.9 }}>
            <button onClick={() => navigate('/dashboard/companies')} className="hover:underline">
              Companies
            </button>
            <span>›</span>
            <span>{company.name}</span>
          </div>

          {/* Company Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
              >
                {getCompanyInitial(company.name)}
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">
                  {company.name}
                </h1>
                <div className="flex items-center gap-4 text-white text-sm" style={{ opacity: 0.9 }}>
                  {company.founding_year && (
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      <span>Founded {company.founding_year}</span>
                    </div>
                  )}
                  {company.industry && (
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                        <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                      </svg>
                      <span>{company.industry}</span>
                    </div>
                  )}
                  {company.location && (
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      <span>{company.location}</span>
                    </div>
                  )}
                </div>

                {/* Tags */}
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFFFFF' }}>
                    {company.industry || 'Technology'}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFFFFF' }}>
                    {company.location || 'Location'}
                  </span>
                  {company.qualification_name && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(255,255,255,0.25)', color: '#FFFFFF', border: '1px solid rgba(255,255,255,0.4)' }}>
                      {company.qualification_name}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard/companies')}
                className="px-6 py-2 rounded-lg font-medium text-white hover:bg-white hover:bg-opacity-20 transition-all"
                style={{ border: '1px solid rgba(255,255,255,0.3)' }}
              >
                ← Back
              </button>
              {canManageCompany && (
                <>
                  <button
                    onClick={() => setShowEditCompanyModal(true)}
                    className="px-5 py-2 rounded-lg font-medium text-white hover:bg-white hover:bg-opacity-20 transition-all"
                    style={{ border: '1px solid rgba(255,255,255,0.3)' }}
                  >
                    Edit Company
                  </button>
                  <button
                    onClick={() => setShowMembersManager(true)}
                    className="px-5 py-2 rounded-lg font-medium text-white hover:bg-white hover:bg-opacity-20 transition-all"
                    style={{ border: '1px solid rgba(255,255,255,0.3)' }}
                  >
                    Manage Members
                  </button>
                  {/* Enrich dropdown — same options as admin */}
                  <div className="relative" onClick={(event) => event.stopPropagation()}>
                    <button
                      onClick={() => setShowEnrichMenu(!showEnrichMenu)}
                      className="flex items-center gap-1.5 px-5 py-2 rounded-lg font-medium text-white hover:bg-white hover:bg-opacity-20 transition-all"
                      style={{ border: '1px solid rgba(255,255,255,0.3)' }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Enrich
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {showEnrichMenu && (
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                        <button
                          onClick={() => { setShowEnrichMenu(false); navigate(`/dashboard/enrichment/${id}`); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
                        >
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Document Enrichment
                        </button>
                        <button
                          onClick={() => { setShowEnrichMenu(false); setEnrichModal({ type: 'web', open: true }); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                          </svg>
                          Web Enrichment
                        </button>
                        <button
                          onClick={() => { setShowEnrichMenu(false); setEnrichModal({ type: 'linkedin', open: true }); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <svg className="w-4 h-4" style={{ color: '#0077B5' }} fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                          </svg>
                          LinkedIn Enrichment
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1400px] mx-auto px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2">
            {/* Navigation Tabs */}
            <div className="border-b mb-6" style={{ borderColor: '#E5E7EB' }}>
              <div className="flex gap-6">
                {['overview', 'about', 'linkedin', 'web-info', 'leadership', 'job-openings', 'similar', 'queries'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`pb-3 px-1 text-sm font-medium capitalize transition-colors whitespace-nowrap ${
                      activeTab === tab
                        ? 'text-blue-600 border-b-2'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    style={activeTab === tab ? { borderBottomColor: COLORS.user.primary } : {}}
                  >
                    {tab === 'similar' ? 'Similar Companies' :
                     tab === 'queries' ? 'Query History' :
                     tab === 'job-openings' ? 'Job Openings' :
                     tab === 'web-info' ? 'Web Info' :
                     tab === 'linkedin' ? 'LinkedIn' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-lg border p-6" style={{ borderColor: '#E5E7EB' }}>
              {activeTab === 'overview' && (
                <CompanyOverviewTab company={company} scoringInProgress={scoringInProgress} />
              )}


              {activeTab === 'linkedin' && (
                <div>
                  <div className="flex items-center gap-2 mb-6">
                    <svg className="w-5 h-5" style={{ color: '#0077B5' }} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    <h3 className="text-lg font-bold text-gray-900">LinkedIn Profile</h3>
                  </div>
                  <LinkedInProfileDisplay linkedInData={linkedInData} isLoading={isLoadingLinkedIn} />
                </div>
              )}

              {activeTab === 'web-info' && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Website Enrichment Data</h3>
                  {webEnrichmentData &&
                    canManageCompany &&
                    company?.id &&
                    Object.keys(webEnrichmentData.pending_web_overrides ?? {}).length > 0 && (
                      <WebOverridePendingReview
                        pendingOverrides={webEnrichmentData.pending_web_overrides!}
                        currentValues={webEnrichmentData.extracted_data ?? {}}
                        companyId={company.id}
                        onResolved={(updated) => setWebEnrichmentData(updated)}
                      />
                    )}
                  <WebEnrichmentDisplay
                    webEnrichmentData={webEnrichmentData}
                    isLoading={isLoadingWebEnrichment}
                    canEdit={canManageCompany}
                    companyId={company?.id}
                    onDataUpdated={(updated) => setWebEnrichmentData(updated)}
                  />
                </div>
              )}


              {activeTab === 'about' && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">About {company.name}</h3>
                  {company.description ? (
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap mb-6">
                      {company.description}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500 italic mb-6">No description available for this company.</p>
                  )}

                  {/* Recent Posts from LinkedIn */}
                  {linkedInData?.extracted_data && (
                    <LinkedInRecentPosts extractedData={linkedInData.extracted_data} />
                  )}
                </div>
              )}

              {activeTab === 'leadership' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">Leadership</h3>
                    {canManageCompany && company?.id && (
                      <button
                        onClick={() => setIsEditingLeadership(!isEditingLeadership)}
                        className="px-4 py-1.5 rounded-lg text-sm font-medium transition-all"
                        style={isEditingLeadership
                          ? { backgroundColor: '#F3F4F6', color: '#374151', border: '1px solid #E5E7EB' }
                          : { backgroundColor: COLORS.user.primary, color: 'white' }
                        }
                      >
                        {isEditingLeadership ? 'Done' : 'Edit'}
                      </button>
                    )}
                  </div>
                  {isEditingLeadership && company?.id ? (
                    <EditLeadershipTab companyId={company.id} />
                  ) : (isLoadingLeaders || isLoadingLinkedIn || isLoadingWebEnrichment) ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-transparent"
                        style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }} />
                    </div>
                  ) : (() => {
                    // Use API leaders first (same as admin), fall back to enrichment
                    const enrichmentLeaders = [
                      ...(Array.isArray(webEnrichmentData?.extracted_data?.leadership_team) ? webEnrichmentData!.extracted_data!.leadership_team : []),
                      ...(Array.isArray(linkedInData?.extracted_data?.leadership) ? linkedInData!.extracted_data!.leadership : []),
                    ].filter((l, i, arr) => {
                      const name = typeof l === 'object' ? l?.name : String(l);
                      return arr.findIndex((x) => (typeof x === 'object' ? x?.name : String(x)) === name) === i;
                    });
                    const leaders = apiLeaders.length > 0 ? apiLeaders : enrichmentLeaders;

                    return leaders.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {leaders.map((leader: any, i: number) => (
                          <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm font-semibold flex-shrink-0">
                              {(typeof leader === 'object' ? leader?.name || '?' : String(leader))[0]?.toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {typeof leader === 'object' ? leader?.name || leader?.full_name || 'Unknown' : String(leader)}
                              </p>
                              {typeof leader === 'object' && (leader?.title || leader?.position) && (
                                <p className="text-xs text-gray-500 truncate">{leader.title || leader.position}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-8">No leadership data available.</p>
                    );
                  })()}
                </div>
              )}


              {activeTab === 'job-openings' && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Job Openings</h3>
                  {isLoadingLinkedIn ? (
                    <div className="flex items-center justify-center py-8">
                      <div
                        className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-transparent"
                        style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
                      />
                    </div>
                  ) : linkedInData?.extracted_data ? (
                    (() => {
                      const jobs = linkedInData.extracted_data?.job_openings || 
                                  linkedInData.extracted_data?.jobs || 
                                  linkedInData.extracted_data?.openings || [];
                      const jobsList = Array.isArray(jobs) ? jobs : 
                                      typeof jobs === 'object' ? Object.values(jobs) : [];
                      
                      return jobsList.length > 0 ? (
                        <div className="space-y-4">
                          {jobsList.map((job: any, index: number) => (
                            <div key={index} className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                              {typeof job === 'object' ? (
                                <>
                                  {job.title && (
                                    <p className="font-medium text-sm mb-1 text-gray-900">
                                      {job.title}
                                    </p>
                                  )}
                                  {job.location && (
                                    <p className="text-xs mb-1 text-gray-600">
                                      📍 {job.location}
                                    </p>
                                  )}
                                  {job.description && (
                                    <p className="text-xs mt-2 text-gray-700">
                                      {job.description}
                                    </p>
                                  )}
                                  {job.url && (
                                    <a
                                      href={job.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-600 hover:underline mt-2 inline-block"
                                    >
                                      View Job
                                    </a>
                                  )}
                                </>
                              ) : (
                                <p className="text-sm text-gray-900">{String(job)}</p>
                              )}
                            </div>
                          ))}
                          </div>
                      ) : (
                        <div className="text-center py-8 text-gray-400">
                          <p className="text-sm">No job openings available</p>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="text-center py-8 text-gray-400">
                      <p className="text-sm">No LinkedIn data available</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'similar' && company.id && (
                <div>
                  <RecommendationsSection
                    companyId={company.id}
                    companyName={company.name}
                    limit={10}
                    onCompanyClick={(companyId) => navigate(`/dashboard/companies/${companyId}`)}
                  />
                </div>
              )}

              {activeTab === 'queries' && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Query History</h3>
                  {queryHistory.length > 0 ? (
                    <div className="space-y-3">
                      {queryHistory.map((query) => (
                        <div
                          key={query.id}
                          className="p-4 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                          style={{ borderColor: '#E5E7EB' }}
                          onClick={() => navigate(`/dashboard/chat/${query.query_id || query.id}`)}
                        >
                          <p className="text-sm font-medium text-gray-900 mb-1">{query.query_text}</p>
                          <p className="text-xs text-gray-500">
                            {query.created_at ? new Date(query.created_at).toLocaleString() : '—'}
                          </p>
                      </div>
                    ))}
                  </div>
                  ) : (
                    <p className="text-sm text-gray-500">No queries yet for this company.</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Query Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border p-6" style={{ borderColor: '#E5E7EB' }}>
              {/* Blue Gradient Bubble */}
              <div className="mb-4 flex justify-center">
                <div className="relative w-24 h-24">
                  <div 
                    className="absolute inset-0 rounded-full blur-2xl opacity-60"
                    style={{ 
                      background: `radial-gradient(circle, ${COLORS.user.primary}40 0%, ${COLORS.user.primaryLight}50 50%, transparent 70%)`
                    }}
                  />
                  <div 
                    className="absolute inset-3 rounded-full"
                    style={{ 
                      background: `radial-gradient(circle at 30% 30%, ${COLORS.user.primaryLight} 0%, ${COLORS.user.primary}CC 50%, ${COLORS.user.primary}AA 100%)`,
                      boxShadow: `0 8px 32px ${COLORS.user.primary}4D, inset 0 -8px 16px rgba(255, 255, 255, 0.3), inset 0 8px 16px ${COLORS.user.primary}33`
                    }}
                  />
                </div>
              </div>
              
              {/* Centered Heading */}
              <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">Ask about {company.name}</h3>

              {/* Query Input */}
              <form onSubmit={handleSubmitQuery} className="mb-6">
                <div className="relative">
                  <input
                    type="text"
                  value={queryText}
                  onChange={(e) => setQueryText(e.target.value)}
                    placeholder="Ask me anything..."
                    disabled={isSubmittingQuery}
                    className="w-full px-4 pr-12 py-3 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#FAFAFA' }}
                />
                <button
                  type="submit"
                    disabled={isSubmittingQuery || !queryText.trim()}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ 
                      backgroundColor: isSubmittingQuery || !queryText.trim() ? '#E5E7EB' : COLORS.user.primary,
                      color: '#FFFFFF'
                    }}
                  >
                    {isSubmittingQuery ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                  )}
                </button>
                </div>
              </form>
          </div>

            {/* LinkedIn Card */}
            {linkedInData && linkedInData.extracted_data && (
              <div className="bg-white rounded-lg border p-6 mt-6" style={{ borderColor: '#E5E7EB' }}>
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-5 h-5" style={{ color: '#0077B5' }} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  <h3 className="text-lg font-bold text-gray-900">LinkedIn</h3>
                </div>

                {/* Helper function to format numbers */}
                {(() => {
                  const formatNumber = (num: number): string => {
                    if (num >= 1000000) {
                      return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
                    }
                    if (num >= 1000) {
                      return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
                    }
                    return num.toString();
                  };

                  const extractedData = linkedInData.extracted_data;
                  const followerCount = extractedData?.follower_count;
                  const jobOpenings = extractedData?.job_openings || [];
                  const leadership = extractedData?.leadership || [];
                  const fundingInfo = extractedData?.funding_info;

                  return (
                    <div className="space-y-4">
                      {/* Stats Row */}
                      <div className="grid grid-cols-2 gap-4">
                        {followerCount !== undefined && followerCount !== null && (
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Followers</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {formatNumber(followerCount)}
                            </p>
              </div>
                        )}
                        {jobOpenings.length > 0 && (
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Open Jobs</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {jobOpenings.length}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Leadership */}
                      {leadership.length > 0 && (
                  <div>
                          <p className="text-xs text-gray-500 mb-2">Key Leadership</p>
                          <div className="flex flex-wrap gap-2">
                            {leadership.slice(0, 5).map((leader: any, index: number) => (
                              <span
                                key={index}
                                className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700"
                              >
                                {leader.name || leader.title || (typeof leader === 'string' ? leader : JSON.stringify(leader))}
                              </span>
                            ))}
                            {leadership.length > 5 && (
                              <span className="px-2 py-1 rounded text-xs font-medium text-gray-500">
                                +{leadership.length - 5} more
                              </span>
                            )}
                    </div>
                  </div>
                )}

                      {/* Funding Info */}
                      {fundingInfo && (
                  <div>
                          <p className="text-xs text-gray-500 mb-2">Funding</p>
                          <div className="space-y-1">
                            {fundingInfo.last_round_raised && (
                              <p className="text-sm font-semibold text-gray-900">
                                {fundingInfo.last_round_raised}
                              </p>
                            )}
                            {fundingInfo.last_round_type && (
                              <p className="text-xs text-gray-600">
                                {fundingInfo.last_round_type}
                                {fundingInfo.last_round_date && (
                                  <span className="ml-2">
                                    ({new Date(fundingInfo.last_round_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })})
                                  </span>
                                )}
                              </p>
                            )}
                            {fundingInfo.rounds && (
                              <p className="text-xs text-gray-600">
                                {fundingInfo.rounds} funding round{fundingInfo.rounds !== 1 ? 's' : ''}
                              </p>
                            )}
                    </div>
                  </div>
                )}
                    </div>
                  );
                })()}

                {/* Specialties */}
                {(() => {
                  const specialties = linkedInData.extracted_data?.specialties || 
                                     linkedInData.extracted_data?.specialities || 
                                     linkedInData.extracted_data?.specializations || [];
                  const specialtiesList = Array.isArray(specialties) ? specialties : 
                                         typeof specialties === 'string' ? specialties.split(',').map(s => s.trim()) : 
                                         typeof specialties === 'object' ? Object.values(specialties) : [];
                  
                  return specialtiesList.length > 0 && (
                    <div className="mt-4 pt-4 border-t" style={{ borderColor: '#E5E7EB' }}>
                      <h4 className="text-sm font-semibold mb-3 text-gray-900">
                        Specialties
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {specialtiesList.map((specialty: any, index: number) => (
                          <span
                            key={index}
                            className="px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700"
                          >
                            {typeof specialty === 'object' ? specialty.name || JSON.stringify(specialty) : String(specialty)}
                          </span>
                        ))}
                  </div>
                    </div>
                  );
                })()}
                  </div>
                )}
          </div>
        </div>
      </div>

      {/* Company Members Manager Drawer */}
      {showMembersManager && company && (
        <CompanyMembersManager
          companyId={company.id}
          currentUserRole={currentUserRole}
          onClose={() => {
            setShowMembersManager(false);
            // Reload company data to refresh user role if it changed
            loadCompanyData();
          }}
        />
      )}

      {/* Edit Company Modal */}
      {showEditCompanyModal && company && (
        <EditCompanyModal
          company={company}
          onClose={() => setShowEditCompanyModal(false)}
          onSaved={(updated) => setCompany(updated)}
          accentColor={COLORS.user.primary}
        />
      )}

      {/* Enrichment Action Modal (Web / LinkedIn) */}
      {enrichModal.open && company && (
        <EnrichmentActionModal
          type={enrichModal.type}
          companyId={company.id}
          initialUrl={
            enrichModal.type === 'web'
              ? company.website || ''
              : linkedInData?.linkedin_url || ''
          }
          onClose={() => setEnrichModal({ ...enrichModal, open: false })}
          onComplete={() => loadCompanyData()}
        />
      )}
    </div>
    </UserSidebarLayout>
  );
};
